import {
  type IPublicClientApplication,
  InteractionRequiredAuthError,
  type SilentRequest,
  BrowserAuthError,
} from "@azure/msal-browser";
import { loginRequest, silentRequest } from "../authConfig";
import { ApiConfig } from "../utils/AuthorizedApiBase";

const createSilentRequest = (
  baseRequest: SilentRequest,
  instance: IPublicClientApplication
): SilentRequest | null => {
  const accounts = instance.getAllAccounts();
  if (!accounts.length) {
    return null;
  }

  return {
    ...baseRequest,
    account: accounts[0],
  };
};

let pendingAcquisition: Promise<string> | undefined;

export const registerAccessTokenProvider = (
  instance: IPublicClientApplication
) => {
  ApiConfig.setAccessTokenProvider(async () => {
    // If there's already a pending token acquisition, return the same promise
    if (pendingAcquisition) {
      return pendingAcquisition;
    }

    pendingAcquisition = (async () => {
      const request = createSilentRequest(silentRequest, instance);
      if (!request) {
        return "";
      }

      try {
        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const interactiveResult = await instance.acquireTokenPopup(loginRequest);
            return interactiveResult.accessToken;
          } catch (popupError) {
            // Handle popup-specific errors
            if (popupError instanceof BrowserAuthError && popupError.errorCode === "user_cancelled") {
              console.log("User cancelled the authentication popup");
              return ""; // Allow retry by returning empty token
            }

            // For all other popup errors (including blocked popups), fallback to redirect
            console.error("Failed to acquire access token via popup", popupError);
            // Clear pendingAcquisition before redirect to allow future requests (in tests) to proceed
            const redirectPromise = instance.acquireTokenRedirect(loginRequest);
            pendingAcquisition = undefined;
            await redirectPromise;
            // Since redirect will reload the page, return a never-resolving promise to prevent hanging requests
            return new Promise<string>(() => {});
          }
        }

        console.error("Failed to acquire access token", error);
        return "";
      }
    })()
      .finally(() => {
        pendingAcquisition = undefined;
      });

    return pendingAcquisition;
  });
};

export const getApiConfig = async (
  instance: IPublicClientApplication
): Promise<ApiConfig> => {
  const request = createSilentRequest(silentRequest, instance);
  if (!request) {
    throw new Error("No authenticated user found");
  }

  try {
    const response = await instance.acquireTokenSilent(request);
    // ApiConfig uses a global accessTokenProvider, so we just return a new instance
    // The token is retrieved via the provider set up in registerAccessTokenProvider
    return new ApiConfig();
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        await instance.acquireTokenPopup(loginRequest);
        return new ApiConfig();
      } catch (popupError) {
        console.error("Failed to acquire access token via popup", popupError);
        throw popupError;
      }
    }

    console.error("Failed to acquire access token", error);
    throw error;
  }
};
